import { apiClient } from "../../../../lib/axios";

// ─── Types ────────────────────────────────────────────────────────────────────

export type BlogPost = {
  id: string;
  title: string;
  excerpt?: string | null;
  image_url?: string | null;
  external_url?: string | null;
  tag?: string | null;
  published_at?: string | null;
  is_active: boolean;
  sort_order: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type ImpactStory = {
  id: string;
  name?: string | null;
  role?: string | null;
  organisation?: string | null;
  quote: string;
  image_url?: string | null;
  metric?: string | null;
  metric_label?: string | null;
  is_active: boolean;
  sort_order: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type NewsSettings = Record<string, string | null>;

export type NewsletterSignup = {
  id: string;
  email: string;
  first_name?: string | null;
  created_at?: string | null;
};

// ─── Blog Posts ───────────────────────────────────────────────────────────────

export const newsFeedAdminApi = {
  listBlogPosts: async (): Promise<BlogPost[]> => {
    const res = await apiClient.get("/admin/news/blog-posts");
    return res.data?.data ?? res.data ?? [];
  },

  createBlogPost: async (payload: Omit<BlogPost, "id" | "created_at" | "updated_at">): Promise<BlogPost> => {
    const res = await apiClient.post("/admin/news/blog-posts", payload);
    return res.data?.data ?? res.data;
  },

  updateBlogPost: async (id: string, payload: Partial<Omit<BlogPost, "id">>): Promise<BlogPost> => {
    const res = await apiClient.patch(`/admin/news/blog-posts/${id}`, payload);
    return res.data?.data ?? res.data;
  },

  deleteBlogPost: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/news/blog-posts/${id}`);
  },

  // ─── Impact Stories ─────────────────────────────────────────────────────────

  listImpactStories: async (): Promise<ImpactStory[]> => {
    const res = await apiClient.get("/admin/news/impact-stories");
    return res.data?.data ?? res.data ?? [];
  },

  createImpactStory: async (payload: Omit<ImpactStory, "id" | "created_at" | "updated_at">): Promise<ImpactStory> => {
    const res = await apiClient.post("/admin/news/impact-stories", payload);
    return res.data?.data ?? res.data;
  },

  updateImpactStory: async (id: string, payload: Partial<Omit<ImpactStory, "id">>): Promise<ImpactStory> => {
    const res = await apiClient.patch(`/admin/news/impact-stories/${id}`, payload);
    return res.data?.data ?? res.data;
  },

  deleteImpactStory: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/news/impact-stories/${id}`);
  },

  // ─── Settings ────────────────────────────────────────────────────────────────

  getSettings: async (): Promise<NewsSettings> => {
    const res = await apiClient.get("/admin/news/settings");
    return res.data?.data ?? res.data ?? {};
  },

  updateSettings: async (payload: NewsSettings): Promise<void> => {
    await apiClient.put("/admin/news/settings", payload);
  },

  // ─── Newsletter Signups ──────────────────────────────────────────────────────

  listNewsletterSignups: async (): Promise<NewsletterSignup[]> => {
    const res = await apiClient.get("/admin/news/newsletter-signups");
    return res.data?.data ?? res.data ?? [];
  },

  deleteNewsletterSignup: async (id: string): Promise<void> => {
    await apiClient.delete(`/admin/news/newsletter-signups/${id}`);
  },
};
